import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, ShoppingBag, Cookie, Users, MapPin, Truck, Heart, MessageSquare, Star, Coins, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

const Card = ({ icon: Icon, label, value, to }) => {
  const inner = (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-all hover:-translate-y-0.5 h-full" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-widest text-gray-500 font-bold">{label}</p>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#FFE4EE] text-[#FF1F87]">
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="font-display font-extrabold text-2xl md:text-3xl mt-3 truncate">{value}</p>
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
};

function EmailHealthBanner() {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/admin/email-health").then(r => setH(r.data)).catch(() => {}); }, []);
  if (!h) return null;
  if (h.verified) {
    return (
      <div className="mb-6 flex items-center gap-3 p-4 rounded-2xl border border-green-200 bg-green-50 text-green-800" data-testid="email-health-banner">
        <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
        <div className="text-sm">
          <p className="font-bold">Email delivery is live.</p>
          <p className="text-green-700/80 text-xs">Sending from {h.sender} · verified domain: {(h.domains || []).join(", ")}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="mb-6 flex items-start gap-3 p-4 rounded-2xl border border-amber-200 bg-amber-50 text-amber-900" data-testid="email-health-banner">
      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="text-sm">
        <p className="font-bold">Customer emails are in sandbox mode.</p>
        <p className="text-amber-800/90 text-xs mt-1">{h.message || "Resend isn't fully configured."} Go to <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline font-bold">resend.com/domains</a>, add <b>sugarysweeteverything.com</b>, and copy the DNS records into your domain registrar. Once verified, customer emails (signup, orders, blasts) will deliver automatically.</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(r => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <h1 className="font-display font-extrabold text-3xl md:text-4xl">Dashboard</h1>
        <p className="text-gray-500 mt-1 text-sm">Snapshot of your bakery this week.</p>
      </div>

      <EmailHealthBanner />

      {!stats ? (
        <p className="text-sm text-gray-500">Loading analytics…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Card icon={DollarSign} label="Revenue This Week" value={`$${stats.revenue_week.toFixed(2)}`} />
            <Card icon={DollarSign} label="Revenue All Time" value={`$${stats.revenue_all.toFixed(2)}`} />
            <Card icon={ShoppingBag} label="Orders This Week" value={stats.orders_week} to="/admin/orders" />
            <Card icon={Users} label="New Customers" value={stats.new_accounts} to="/admin/users" />
            <Card icon={Cookie} label="Cookies Sold" value={stats.cookies_sold} />
            <Card icon={Star} label="Most Popular" value={stats.most_popular_flavor} />
            <Card icon={MapPin} label="Pickup Orders" value={stats.pickup_orders} />
            <Card icon={Truck} label="Delivery Orders" value={stats.delivery_orders} />
            <Card icon={Coins} label="Tips Earned" value={`$${stats.tips_earned.toFixed(2)}`} />
            <Card icon={DollarSign} label="Delivery Fees" value={`$${stats.delivery_fees.toFixed(2)}`} />
            <Card icon={Heart} label="Pending Custom" value={stats.pending_custom_requests} to="/admin/custom-orders" />
            <Card icon={MessageSquare} label="Pending Messages" value={stats.pending_messages} to="/admin/messages" />
          </div>

          {(Object.keys(stats.flavor_counts || {}).length > 0 || Object.keys(stats.box_counts || {}).length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h3 className="font-display font-bold text-lg mb-3">Flavor Counts</h3>
                <ul className="space-y-2 text-sm">
                  {Object.entries(stats.flavor_counts).length === 0 && <li className="text-gray-400">No orders yet</li>}
                  {Object.entries(stats.flavor_counts).map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between"><span>{k}</span><span className="font-bold">{v}</span></li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h3 className="font-display font-bold text-lg mb-3">Box Counts</h3>
                <ul className="space-y-2 text-sm">
                  {Object.entries(stats.box_counts).length === 0 && <li className="text-gray-400">No orders yet</li>}
                  {Object.entries(stats.box_counts).map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between"><span>{k}</span><span className="font-bold">{v}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
